import React from "react";

// API functions for expenses management
const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7204';

/**
 * Add a new expense
 * @param {Object} expensesData - Expense data
 * @returns {Promise<Object>} - API response
 */
export const addExpenses = async (expensesData) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log('AddExpenses - Sending data:', expensesData);

    const response = await fetch(`${API_URL}/api/Expenses/AddExpenses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(expensesData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      console.error('AddExpenses API Error:', {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText,
        requestData: expensesData
      });
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing expense
 * @param {Object} expensesData - Expense data with ExpenseID
 * @returns {Promise<Object>} - API response
 */
export const updateExpenses = async (expensesData) => {
  try {
    // Ensure ExpenseID is included
    if (!expensesData.ExpenseID) {
      throw new Error('ExpenseID is required for update');
    }

    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Expenses/UpdateExpenses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(expensesData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Delete an expense by ID
 * @param {number} id - Expense ID to delete
 * @param {boolean} stopPermanentPayment - Whether to stop permanent payment (default: false)
 * @returns {Promise<Object>} - API response
 */
export const deleteExpenses = async (id, stopPermanentPayment = false) => {
  try {
    if (!id) {
      throw new Error('Expense ID is required for deletion');
    }

    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    // Create ExpensesDTO object for deletion with all required fields
    const expenseDTO = {
      ExpenseID: id,
      BuildingID: null,
      Date: null,
      Category: '',
      Description: '',
      Amount: 0,  // Changed from null to 0 since Amount is decimal (non-nullable)
      PaidTo: '',
      ExpenseType: '',
      PaymentMethod: '',
      ReceiptImageURL: '',
      SupplierID: null,
      Vendor: '',
      InvoiceNumber: '',
      StopPermanentPayment: stopPermanentPayment
    };

    const response = await fetch(`${API_URL}/api/Expenses/DeleteExpenses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(expenseDTO),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Alternative delete function using HTTP DELETE method
 * @param {number} id - Expense ID to delete
 * @returns {Promise<Object>} - API response
 */
export const deleteExpenseById = async (id) => {
  try {
    if (!id) {
      throw new Error('Expense ID is required for deletion');
    }

    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${API_URL}/api/Expenses/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Get all expenses for a specific building
 * @param {number} buildingId - Building ID
 * @returns {Promise<Object>} - API response with expenses array
 */
export const getExpenses = async (buildingId) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    if (!buildingId) {
      throw new Error('Building ID is required');
    }

    const response = await fetch(`${API_URL}/api/Expenses/GetExpenses/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

/**
 * Get all expenses for a specific building (External Dashboard - No Auth Required)
 * @param {number} buildingId - Building ID
 * @returns {Promise<Object>} - API response with expenses array
 */
export const getExpensesExternalDashboard = async (buildingId) => {
  try {
    if (!buildingId) {
      throw new Error('Building ID is required');
    }

    const response = await fetch(`${API_URL}/api/ExternalDashboard/GetExpensesExternalDashboard/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
        // Removed Authorization header for external dashboard
      },
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    throw error;
  }
};

// ========================================
// NEW RECURRING EXPENSES FUNCTIONS
// ========================================

/**
 * Get recurring expenses that are due for the current month
 * @param {number} buildingId - Building ID
 * @param {AbortSignal} signal - Optional AbortController signal for cancelling request
 * @returns {Promise<Array>} - Array of due recurring expenses
 */
export const getRecurringExpensesDue = async (buildingId, signal) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    if (!buildingId) {
      throw new Error('Building ID is required');
    }

    const response = await fetch(`${API_URL}/api/Expenses/GetRecurringExpensesDue/${buildingId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      signal // Pass the AbortController signal
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    const result = await response.json();
    return result.data || [];
  } catch (error) {
    // Don't log aborted requests as errors
    if (error.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching recurring expenses due:', error);
    throw error;
  }
};

/**
 * Create recurring expenses for selected expenses with new invoice numbers
 * @param {Array<Object>} expensesData - Array of complete ExpensesDTO objects
 * @returns {Promise<Object>} - API response with created expenses
 */
export const createRecurringExpenses = async (expensesData) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    if (!expensesData || !Array.isArray(expensesData) || expensesData.length === 0) {
      throw new Error('Expenses data array is required');
    }

    console.log('Sending to API:', expensesData);

    const response = await fetch(`${API_URL}/api/Expenses/CreateRecurringExpense`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(expensesData),
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      console.error('Status:', response.status);
      throw new Error(`שגיאת שרת: ${response.status} - ${errorText}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating recurring expenses:', error);
    throw error;
  }
};

/**
 * Update invoice number for an expense
 * @param {number} expenseId - Expense ID to update
 * @param {string} invoiceNumber - New invoice number
 * @returns {Promise<Object>} - API response
 */
export const updateExpenseInvoiceNumber = async (expenseId, invoiceNumber) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    if (!expenseId) {
      throw new Error('Expense ID is required');
    }

    const response = await fetch(`${API_URL}/api/Expenses/UpdateInvoiceNumber/${expenseId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ invoiceNumber }),
    });

    if (!response.ok) {
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating invoice number:', error);
    throw error;
  }
};

/**
 * Stop permanent payment for an expense
 * @param {number} expenseId - Expense ID to stop permanent payment for
 * @returns {Promise<Object>} - API response
 */
export const stopPermanentPayment = async (expenseId) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('No authentication token found');
    }

    if (!expenseId) {
      throw new Error('Expense ID is required');
    }

    const response = await fetch(`${API_URL}/api/Expenses/StopPermanentPayment/${expenseId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    // Check if response is successful
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`שגיאת שרת: ${response.status}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      throw new Error('השרת החזיר תשובה לא תקינה');
    }

    return await response.json();
  } catch (error) {
    console.error('Error stopping permanent payment:', error);
    throw error;
  }
};

// ========================================
// CONVENIENCE FUNCTIONS (Alternative names)
// ========================================

/**
 * Alias for addExpenses to match the new naming convention
 */
export const addExpense = addExpenses;

/**
 * Alias for updateExpenses to match the new naming convention
 */
export const updateExpense = updateExpenses;

/**
 * Alias for deleteExpenses to match the new naming convention
 */
export const deleteExpense = deleteExpenses;